import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  ArrowLeftIcon, 
  DocumentTextIcon, 
  ChartBarIcon,
  EyeIcon,
  UserGroupIcon,
  UserIcon,
  ClockIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { useFirestore } from '../hooks/useFirestore';
import { useBlogAuthors } from '../hooks/useBlogAuthors';
import { useAuth } from '../contexts/AuthContext';
import BlogTable from '../components/BlogTable';
import BlogModal from '../components/BlogModal';
import ConfirmDialog from '../components/ConfirmDialog';

const BlogCollectionPage = () => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    documents,
    loading,
    addDocument,
    updateDocument,
    deleteDocument
  } = useFirestore('blogs');

  // Use auth context for role-based access control
  const { currentUser, userRole, isAdmin } = useAuth();

  // Filter documents based on user role
  const filteredDocuments = useMemo(() => {
    if (isAdmin) {
      return documents; // Admins can see all blogs
    } else if (userRole === 'author' && currentUser) {
      return documents.filter(doc => {
        // Check if user owns this blog by userId or author field
        const isOwner = doc.userId === currentUser.uid || 
                       doc.author === currentUser.uid ||
                       doc.author === currentUser.email ||
                       doc.author === currentUser.displayName;
        return isOwner;
      });
    }
    return []; // Other roles see no blogs
  }, [documents, isAdmin, userRole, currentUser]);

  // Use blog authors hook for enhanced author management
  const { getAuthorStats, getAuthorId, loading: authorsLoading } = useBlogAuthors(filteredDocuments);

  const handleAdd = () => {
    setSelectedDocument(null);
    setModalMode('add');
    setModalOpen(true);
  };

  const handleEdit = (doc) => {
    // Check if user has permission to edit this blog
    if (!isAdmin && userRole === 'author') {
      const isOwner = doc.userId === currentUser?.uid || 
                     doc.author === currentUser?.uid ||
                     doc.author === currentUser?.email ||
                     doc.author === currentUser?.displayName;
      if (!isOwner) {
        alert('You do not have permission to edit this blog post.');
        return;
      }
    }
    setSelectedDocument(doc);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleView = (doc) => {
    setSelectedDocument(doc);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    // Find the blog document to check permissions
    const blogDoc = documents.find(doc => doc.id === id);
    if (!blogDoc) {
      alert('Blog post not found.');
      return;
    }
    
    // Check if user has permission to delete this blog
    if (!isAdmin && userRole === 'author') {
      const isOwner = blogDoc.userId === currentUser?.uid || 
                     blogDoc.author === currentUser?.uid ||
                     blogDoc.author === currentUser?.email ||
                     blogDoc.author === currentUser?.displayName;
      if (!isOwner) {
        alert('You do not have permission to delete this blog post.');
        return;
      }
    }
    
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      setDeleteLoading(true);
      try {
        await deleteDocument(deleteId);
        setConfirmDialogOpen(false);
        setDeleteId(null);
      } catch (error) {
        console.error('Error deleting document:', error);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (modalMode === 'add') {
        // For new blogs, ensure both userId and author fields are set
        if (currentUser) {
          if (!data.userId) {
            data.userId = currentUser.uid;
          }
          if (!data.author) {
            data.author = currentUser.email || currentUser.displayName || currentUser.uid;
          }
        }
        await addDocument(data);
      } else if (modalMode === 'edit') {
        // For editing, ensure the user has permission
        if (!isAdmin && userRole === 'author') {
          const isOwner = selectedDocument.userId === currentUser?.uid || 
                         selectedDocument.author === currentUser?.uid ||
                         selectedDocument.author === currentUser?.email ||
                         selectedDocument.author === currentUser?.displayName;
          if (!isOwner) {
            alert('You do not have permission to edit this blog post.');
            return;
          }
        }
        await updateDocument(selectedDocument.id, data);
      }
    } catch (error) {
      console.error('Error saving document:', error);
      throw error;
    }
  };

  // Calculate blog analytics
  const blogStats = React.useMemo(() => {
    const stats = {
      total: filteredDocuments.length,
      published: 0,
      drafts: 0,
      archived: 0,
      totalWords: 0,
      totalViews: 0,
      authors: new Set(),
      tags: new Set(),
      avgWordsPerPost: 0,
      postsThisMonth: 0
    };

    const thisMonth = new Date();
    thisMonth.setDate(1);

    filteredDocuments.forEach(doc => {
      // Status counts
      if (doc.status === 'published') stats.published++;
      else if (doc.status === 'archived') stats.archived++;
      else stats.drafts++;

      // Word count (strip HTML tags if content is HTML)
      const wordCount = doc.wordCount || (doc.content ? doc.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0);
      stats.totalWords += wordCount;

      // Views
      stats.totalViews += doc.views || 0;

      // Authors
      const authorId = getAuthorId(doc);
      if (authorId) stats.authors.add(authorId);

      // Tags
      if (doc.tags) {
        const tags = typeof doc.tags === 'string' ? doc.tags.split(',') : doc.tags;
        tags.forEach(tag => stats.tags.add(tag.trim()));
      }

      // Posts this month
      const postDate = doc.timestamp?._seconds ? new Date(doc.timestamp._seconds * 1000) : 
                      doc.createdAt?._seconds ? new Date(doc.createdAt._seconds * 1000) : null;
      if (postDate && postDate >= thisMonth) {
        stats.postsThisMonth++;
      }
    });

    stats.avgWordsPerPost = stats.total > 0 ? Math.round(stats.totalWords / stats.total) : 0;

    return stats;
  }, [filteredDocuments]);

  // Get author statistics
  const authorStats = React.useMemo(() => {
    if (authorsLoading) return { totalAuthors: 0, activeAuthors: 0, topAuthors: [] };
    return getAuthorStats();
  }, [getAuthorStats, authorsLoading]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-8 w-8 mr-3 text-indigo-600" />
                  Blog Management
                </h1>
                <p className="text-sm text-gray-600">
                  Manage your blog posts, articles, and content
                </p>
              </div>
            </div>
            {(isAdmin || userRole === 'author') && (
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Blog Post
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analytics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Posts */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Posts</p>
                <p className="text-2xl font-bold text-gray-900">{blogStats.total}</p>
                <p className="text-sm text-green-600">+{blogStats.postsThisMonth} this month</p>
              </div>
            </div>
          </div>

          {/* Published Posts */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <EyeIcon className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-2xl font-bold text-gray-900">{blogStats.published}</p>
                <p className="text-sm text-gray-500">{blogStats.drafts} drafts</p>
              </div>
            </div>
          </div>

          {/* Authors */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserGroupIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Authors</p>
                <p className="text-2xl font-bold text-gray-900">
                  {authorsLoading ? (
                    <div className="animate-pulse bg-gray-200 h-8 w-12 rounded"></div>
                  ) : (
                    authorStats.activeAuthors
                  )}
                </p>
                <p className="text-sm text-gray-500">{blogStats.tags.size} tags</p>
              </div>
            </div>
          </div>

          {/* Content Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Words</p>
                <p className="text-2xl font-bold text-gray-900">{blogStats.avgWordsPerPost.toLocaleString()}</p>
                <p className="text-sm text-gray-500">{blogStats.totalWords.toLocaleString()} total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Insights */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2 text-indigo-600" />
            Content Insights
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Status Distribution */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Status Distribution</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Published</span>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${blogStats.total > 0 ? (blogStats.published / blogStats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{blogStats.published}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Drafts</span>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-yellow-500 h-2 rounded-full" 
                        style={{ width: `${blogStats.total > 0 ? (blogStats.drafts / blogStats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{blogStats.drafts}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Archived</span>
                  <div className="flex items-center">
                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-gray-500 h-2 rounded-full" 
                        style={{ width: `${blogStats.total > 0 ? (blogStats.archived / blogStats.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{blogStats.archived}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Authors */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Top Authors</h4>
              {authorsLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center space-x-2">
                      <div className="bg-gray-200 h-4 w-4 rounded-full"></div>
                      <div className="bg-gray-200 h-4 w-20 rounded"></div>
                      <div className="bg-gray-200 h-4 w-8 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {authorStats.topAuthors.slice(0, 5).map((author, index) => (
                    <div key={author.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-900 truncate max-w-[100px]">
                          {author.name || author.email?.split('@')[0] || 'Unknown'}
                        </span>
                      </div>
                      <span className="text-gray-500 font-medium">{author.postCount} posts</span>
                    </div>
                  ))}
                  {authorStats.topAuthors.length === 0 && (
                    <p className="text-gray-500 text-sm">No authors found</p>
                  )}
                </div>
              )}
            </div>

            {/* Popular Tags */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Popular Tags</h4>
              <div className="flex flex-wrap gap-2">
                {Array.from(blogStats.tags).slice(0, 8).map((tag, index) => (
                  <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    <TagIcon className="h-3 w-3 mr-1" />
                    {tag}
                  </span>
                ))}
                {blogStats.tags.size > 8 && (
                  <span className="text-xs text-gray-500">+{blogStats.tags.size - 8} more</span>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Activity</h4>
              <div className="space-y-2">
                {filteredDocuments
                  .filter(doc => doc.updatedAt || doc.timestamp)
                  .sort((a, b) => {
                    const aTime = (a.updatedAt || a.timestamp)?._seconds || 0;
                    const bTime = (b.updatedAt || b.timestamp)?._seconds || 0;
                    return bTime - aTime;
                  })
                  .slice(0, 3)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600 truncate">
                        {doc.title ? doc.title.substring(0, 30) + '...' : 'Untitled'}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Role-based Access Control Info */}
        {!isAdmin && userRole === 'author' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserIcon className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Author Access</h3>
                <p className="text-sm text-blue-700">
                  You can only view, edit, and delete your own blog posts. Contact an administrator for additional permissions.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Blog Table */}
        <BlogTable
          documents={filteredDocuments}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          loading={loading}
        />

        {/* Modals */}
        <BlogModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
          document={selectedDocument}
          mode={modalMode}
        />

        <ConfirmDialog
          isOpen={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Blog Post"
          message="Are you sure you want to delete this blog post? This action cannot be undone."
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default BlogCollectionPage;
